import React, { useState, useEffect, useCallback } from 'react';
import { useNavigate } from 'react-router-dom';
import {
  Box, Typography, Card, CardContent, Grid, CircularProgress, Alert,
  Chip, Stack, LinearProgress, useTheme, Button, Divider, Tooltip,
} from '@mui/material';
import {
  ResponsiveContainer, PieChart, Pie, Cell, Tooltip as RTooltip,
  LineChart, Line, XAxis, YAxis, CartesianGrid,
} from 'recharts';
import DirectionsBoatIcon from '@mui/icons-material/DirectionsBoat';
import ArrowForwardIcon from '@mui/icons-material/ArrowForward';
import LocalFireDepartmentIcon from '@mui/icons-material/LocalFireDepartment';
import HistoryIcon from '@mui/icons-material/History';
import BookIcon from '@mui/icons-material/Book';
import apiClient from '../services/apiClient';
import { useLanguage } from '../context/LanguageContext';
import { useAuth } from '../context/AuthContext';

/**
 * ProgressPage — "Your Chart": a map of the voyage, not a scoreboard.
 *
 * Sections:
 *   1. Voyage arc  — the CEFR journey A1 -> C2 with the learner's ship placed
 *      at their current level (answers "where am I going?").
 *   2. Momentum    — streak + a 13-week activity heat-map.
 *   3. Holdings    — headline stats, each one a DOOR back into practice.
 *   4. Details     — vocabulary mix + journal fluency trend.
 *   5. Logbook     — recent sessions + journal entry points.
 */

const CEFR = ['A1', 'A2', 'B1', 'B2', 'C1', 'C2'];
const CEFR_LABEL = {
  A0: 'Absolute beginner', A1: 'Beginner', A2: 'Elementary', B1: 'Intermediate',
  B2: 'Upper intermediate', C1: 'Advanced', C2: 'Proficient',
};

function fmtTime(seconds) {
  const s = seconds || 0;
  if (s < 60) return `${s}s`;
  const m = Math.round(s / 60);
  if (m < 60) return `${m} min`;
  const h = Math.floor(m / 60);
  return `${h}h ${m % 60}m`;
}

/* ---------- Voyage arc (CEFR journey) ---------- */
function VoyageArc({ level, theme }) {
  const idx = Math.max(0, CEFR.indexOf(level)); // A0 or unknown -> before A1
  const reached = CEFR.indexOf(level); // -1 if A0/unknown
  return (
    <Card
      elevation={0}
      sx={{
        border: '1px solid', borderColor: 'rgba(198,152,43,0.35)', borderRadius: 3,
        color: '#fff', overflow: 'hidden',
        background:
          'radial-gradient(700px 260px at 85% -30%, rgba(198,152,43,0.30), transparent 60%),' +
          'linear-gradient(160deg, #0A3161 0%, #072347 100%)',
      }}
    >
      <CardContent sx={{ py: 3 }}>
        <Typography variant="overline" sx={{ letterSpacing: '0.22em', color: 'rgba(233,209,138,0.9)' }}>
          Where you are
        </Typography>
        <Typography variant="h4" sx={{ fontFamily: '"Fraunces", Georgia, serif', fontWeight: 600, mt: 0.5 }}>
          {level || 'A0'} · {CEFR_LABEL[level] || 'Setting sail'}
        </Typography>

        {/* Ports along the route */}
        <Box sx={{ position: 'relative', mt: 3, px: 1 }}>
          {/* dotted route line */}
          <Box sx={{
            position: 'absolute', left: 12, right: 12, top: 13, height: 0,
            borderTop: '2px dotted rgba(255,255,255,0.35)',
          }} />
          <Box sx={{ display: 'flex', justifyContent: 'space-between', position: 'relative' }}>
            {CEFR.map((port, i) => {
              const done = i <= reached;
              const current = i === reached;
              return (
                <Box key={port} sx={{ display: 'flex', flexDirection: 'column', alignItems: 'center', flex: 1 }}>
                  <Box sx={{
                    width: 26, height: 26, borderRadius: '50%',
                    bgcolor: current ? '#E9D18A' : (done ? 'rgba(233,209,138,0.55)' : 'rgba(255,255,255,0.12)'),
                    border: current ? '2px solid #E9D18A' : '2px solid rgba(255,255,255,0.35)',
                    display: 'flex', alignItems: 'center', justifyContent: 'center',
                    boxShadow: current ? '0 0 0 5px rgba(233,209,138,0.25)' : 'none',
                    zIndex: 1,
                  }}>
                    {current && <DirectionsBoatIcon sx={{ fontSize: 16, color: '#072347' }} />}
                  </Box>
                  <Typography variant="caption" sx={{
                    mt: 0.75, fontWeight: current ? 700 : 500,
                    color: current ? '#fff' : 'rgba(255,255,255,0.7)',
                  }}>
                    {port}
                  </Typography>
                </Box>
              );
            })}
          </Box>
        </Box>
        <Typography variant="body2" sx={{ mt: 2, color: 'rgba(255,255,255,0.75)' }}>
          {reached >= CEFR.length - 1
            ? 'You’ve reached the final port — keep your skills sharp.'
            : `Next port: ${CEFR[Math.max(0, reached + 1)]}. Every conversation and story moves you closer.`}
        </Typography>
      </CardContent>
    </Card>
  );
}

/* ---------- Activity heat-map (last 13 weeks) ---------- */
function ActivityHeatmap({ days, theme }) {
  // The API sends [{ date, count }]; accept plain date strings too.
  const set = new Set((days || []).map((d) => (typeof d === 'string' ? d : d?.date)));
  const cells = [];
  const today = new Date();
  // 13 weeks * 7 = 91 days, oldest first
  for (let i = 90; i >= 0; i--) {
    const d = new Date(today);
    d.setDate(today.getDate() - i);
    const key = d.toISOString().slice(0, 10);
    cells.push({ key, active: set.has(key) });
  }
  // group into columns of 7
  const cols = [];
  for (let i = 0; i < cells.length; i += 7) cols.push(cells.slice(i, i + 7));
  return (
    <Box sx={{ display: 'flex', gap: '3px', overflowX: 'auto', pb: 0.5 }}>
      {cols.map((col, ci) => (
        <Box key={ci} sx={{ display: 'flex', flexDirection: 'column', gap: '3px' }}>
          {col.map((c) => (
            <Tooltip key={c.key} title={c.key} arrow disableInteractive>
              <Box sx={{
                width: 12, height: 12, borderRadius: '3px',
                bgcolor: c.active ? theme.palette.secondary.main : 'rgba(10,49,97,0.08)',
                border: c.active ? 'none' : '1px solid rgba(10,49,97,0.06)',
              }} />
            </Tooltip>
          ))}
        </Box>
      ))}
    </Box>
  );
}

/* ---------- A stat tile that is also a door ---------- */
function ActionStat({ value, label, cta, color = 'primary.main', onClick }) {
  return (
    <Card
      elevation={0}
      sx={{
        border: '1px solid', borderColor: 'divider', borderRadius: 2, height: '100%',
        transition: 'all .15s', cursor: onClick ? 'pointer' : 'default',
        '&:hover': onClick ? { borderColor: 'primary.main', boxShadow: 2, transform: 'translateY(-2px)' } : {},
      }}
      onClick={onClick}
    >
      <CardContent sx={{ py: 2 }}>
        <Typography variant="h4" sx={{ fontWeight: 700, color, lineHeight: 1.1, fontFamily: '"Fraunces", Georgia, serif' }}>
          {value}
        </Typography>
        <Typography variant="body2" color="text.secondary" sx={{ mt: 0.5 }}>{label}</Typography>
        {cta && (
          <Stack direction="row" alignItems="center" spacing={0.5} sx={{ mt: 1, color: 'primary.main' }}>
            <Typography variant="caption" sx={{ fontWeight: 700 }}>{cta}</Typography>
            <ArrowForwardIcon sx={{ fontSize: 14 }} />
          </Stack>
        )}
      </CardContent>
    </Card>
  );
}

export default function ProgressPage() {
  const theme = useTheme();
  const navigate = useNavigate();
  const { selectedLanguage, getLanguageDisplayName } = useLanguage();
  const { user } = useAuth();
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState(null);
  const [data, setData] = useState(null);
  const [recentSessions, setRecentSessions] = useState([]);

  const fetchProgress = useCallback(async () => {
    setLoading(true);
    setError(null);
    try {
      const res = await apiClient.progress.getProgress(selectedLanguage);
      setData(res.data);
      // Logbook preview — best effort, never blocks the page.
      try {
        const s = await apiClient.sessions.getUserSessions();
        const all = (s.data.sessions || [])
          .filter((x) => !selectedLanguage || x.target_language === selectedLanguage)
          .sort((a, b) => new Date(b.updated_at) - new Date(a.updated_at))
          .slice(0, 4);
        setRecentSessions(all);
      } catch (_) { /* ignore */ }
    } catch (e) {
      console.error('Failed to load progress', e);
      setError('Could not load your progress right now.');
    } finally {
      setLoading(false);
    }
  }, [selectedLanguage]);

  useEffect(() => { fetchProgress(); }, [fetchProgress]);

  if (loading) {
    return <Box sx={{ display: 'flex', justifyContent: 'center', py: 10 }}><CircularProgress /></Box>;
  }
  if (error) {
    return <Box sx={{ p: 2 }}><Alert severity="error">{error}</Alert></Box>;
  }

  const d = data || {};
  const vocab = d.vocabulary || {};
  const stories = d.stories || {};
  const journal = d.journal || {};
  const sessions = d.sessions || {};
  const activity = d.activity || {};

  // Current CEFR level for the selected language (from the user's profile).
  const langEntry = (user?.languages || []).find((l) => l.language_code === selectedLanguage);
  const currentLevel = langEntry?.proficiency_level || 'A0';

  const langName = (getLanguageDisplayName && getLanguageDisplayName(selectedLanguage)) || selectedLanguage;

  const vocabData = [
    { name: 'New', value: vocab.level_1 || 0, color: theme.palette.error.main },
    { name: 'Learning', value: vocab.level_2 || 0, color: theme.palette.warning.main },
    { name: 'Known', value: vocab.level_3 || 0, color: theme.palette.success.main },
  ].filter((x) => x.value > 0);

  const fluency = (journal.fluency_trend || []).map((p, i) => ({
    idx: i + 1, date: p.date, fluency_score: p.fluency_score,
  }));

  const storyPct = stories.stories_total
    ? Math.round((stories.stories_completed / stories.stories_total) * 100) : 0;

  return (
    <Box sx={{ p: { xs: 1, sm: 2 }, pb: 8, maxWidth: 1100, mx: 'auto' }}>
      <Typography variant="h4" sx={{ fontFamily: '"Fraunces", Georgia, serif', fontWeight: 600, mb: 0.25 }}>
        Your Chart
      </Typography>
      <Typography variant="body2" color="text.secondary" sx={{ mb: 2 }}>
        {langName} · the map of your voyage
      </Typography>

      {/* 1 · Voyage arc */}
      <Box sx={{ mb: 2 }}>
        <VoyageArc level={currentLevel} theme={theme} />
      </Box>

      <Grid container spacing={2}>
        {/* 2 · Momentum */}
        <Grid item xs={12} md={7}>
          <Card elevation={0} sx={{ border: '1px solid', borderColor: 'divider', borderRadius: 2, height: '100%' }}>
            <CardContent>
              <Stack direction="row" alignItems="center" spacing={1} sx={{ mb: 1.5 }}>
                <LocalFireDepartmentIcon sx={{ color: theme.palette.warning.main }} />
                <Typography variant="subtitle1" sx={{ fontWeight: 700 }}>
                  {activity.current_streak || 0}-day streak
                </Typography>
                <Typography variant="body2" color="text.secondary">
                  · {(activity.days || []).length} active days (90d)
                </Typography>
              </Stack>
              <ActivityHeatmap days={activity.days} theme={theme} />
            </CardContent>
          </Card>
        </Grid>

        {/* Story progress */}
        <Grid item xs={12} md={5}>
          <Card elevation={0} sx={{ border: '1px solid', borderColor: 'divider', borderRadius: 2, height: '100%' }}>
            <CardContent>
              <Typography variant="subtitle1" sx={{ fontWeight: 700, mb: 1 }}>Voyage progress</Typography>
              <Box sx={{ display: 'flex', justifyContent: 'space-between', mb: 0.5 }}>
                <Typography variant="body2" color="text.secondary">
                  {stories.stories_completed || 0} of {stories.stories_total || 0} stories
                </Typography>
                <Typography variant="body2" sx={{ fontWeight: 700 }}>{storyPct}%</Typography>
              </Box>
              <LinearProgress variant="determinate" value={storyPct}
                sx={{ height: 8, borderRadius: 4, mb: 1.5,
                  '& .MuiLinearProgress-bar': { background: `linear-gradient(90deg, ${theme.palette.secondary.dark}, ${theme.palette.secondary.main})` } }} />
              <Typography variant="body2" color="text.secondary">
                {stories.paths_completed || 0} voyages done · {stories.paths_in_progress || 0} underway · {fmtTime(stories.time_spent_seconds)} at sea
              </Typography>
            </CardContent>
          </Card>
        </Grid>
      </Grid>

      {/* 3 · Holdings — each tile routes back into practice */}
      <Typography variant="subtitle2" color="text.secondary" sx={{ mt: 3, mb: 1, letterSpacing: '0.08em' }}>
        YOUR HOLDINGS
      </Typography>
      <Grid container spacing={2}>
        <Grid item xs={6} sm={3}>
          <ActionStat value={vocab.due_count || 0} label="Words due" color="warning.main"
            cta={(vocab.due_count || 0) > 0 ? 'Review now' : 'All caught up'}
            onClick={() => navigate('/vocabulary/review')} />
        </Grid>
        <Grid item xs={6} sm={3}>
          <ActionStat value={vocab.total || 0} label="Words total" color="primary.main"
            cta="Open Words" onClick={() => navigate('/vocabulary')} />
        </Grid>
        <Grid item xs={6} sm={3}>
          <ActionStat value={stories.stories_completed || 0} label="Stories done" color="success.main"
            cta="Continue a Voyage" onClick={() => navigate('/breadcrumbs')} />
        </Grid>
        <Grid item xs={6} sm={3}>
          <ActionStat value={sessions.conversations || 0}
            label={`Conversations · ${sessions.minutes_estimated ? '~' : ''}${sessions.conversation_minutes || 0} min`}
            color="secondary.dark" cta="Start one" onClick={() => navigate('/explore')} />
        </Grid>
      </Grid>

      {/* 4 · Details */}
      <Grid container spacing={2} sx={{ mt: 0.5 }}>
        <Grid item xs={12} md={6}>
          <Card elevation={0} sx={{ border: '1px solid', borderColor: 'divider', borderRadius: 2, height: '100%' }}>
            <CardContent>
              <Typography variant="subtitle1" sx={{ fontWeight: 700, mb: 1 }}>Vocabulary mix</Typography>
              {vocabData.length === 0 ? (
                <Typography variant="body2" color="text.secondary" sx={{ py: 6, textAlign: 'center' }}>
                  No words yet — mine them from a conversation or story.
                </Typography>
              ) : (
                <ResponsiveContainer width="100%" height={220}>
                  <PieChart>
                    <Pie data={vocabData} dataKey="value" nameKey="name" innerRadius={55} outerRadius={88} paddingAngle={2}>
                      {vocabData.map((entry) => <Cell key={entry.name} fill={entry.color} />)}
                    </Pie>
                    <RTooltip />
                  </PieChart>
                </ResponsiveContainer>
              )}
              <Stack direction="row" spacing={2} justifyContent="center" sx={{ mt: 1 }}>
                <LegendDot color={theme.palette.error.main} label={`New ${vocab.level_1 || 0}`} />
                <LegendDot color={theme.palette.warning.main} label={`Learning ${vocab.level_2 || 0}`} />
                <LegendDot color={theme.palette.success.main} label={`Known ${vocab.level_3 || 0}`} />
              </Stack>
            </CardContent>
          </Card>
        </Grid>

        <Grid item xs={12} md={6}>
          <Card elevation={0} sx={{ border: '1px solid', borderColor: 'divider', borderRadius: 2, height: '100%' }}>
            <CardContent>
              <Typography variant="subtitle1" sx={{ fontWeight: 700, mb: 1 }}>
                Journal fluency
                {journal.avg_fluency != null && (
                  <Typography component="span" variant="body2" color="text.secondary" sx={{ ml: 1 }}>
                    avg {journal.avg_fluency}
                  </Typography>
                )}
              </Typography>
              {fluency.length === 0 ? (
                <Typography variant="body2" color="text.secondary" sx={{ py: 6, textAlign: 'center' }}>
                  Write journal entries to track your fluency trend.
                </Typography>
              ) : (
                <ResponsiveContainer width="100%" height={220}>
                  <LineChart data={fluency} margin={{ top: 8, right: 8, left: -16, bottom: 0 }}>
                    <CartesianGrid strokeDasharray="3 3" stroke={theme.palette.divider} />
                    <XAxis dataKey="idx" tick={{ fontSize: 12 }} />
                    <YAxis domain={[0, 100]} tick={{ fontSize: 12 }} />
                    <RTooltip />
                    <Line type="monotone" dataKey="fluency_score" stroke={theme.palette.primary.main} strokeWidth={2} dot={{ r: 2 }} />
                  </LineChart>
                </ResponsiveContainer>
              )}
            </CardContent>
          </Card>
        </Grid>
      </Grid>

      {/* Recurring weak points (tracked across conversation summaries) */}
      <Card elevation={0} sx={{ border: '1px solid', borderColor: 'divider', borderRadius: 2, mt: 2 }}>
        <CardContent>
          <Typography variant="subtitle1" sx={{ fontWeight: 700, mb: 1 }}>Recurring weak points</Typography>
          {(d.weak_points || []).length === 0 ? (
            <Typography variant="body2" color="text.secondary">
              After a conversation, open its summary; the areas you struggled with are tracked here across sessions.
            </Typography>
          ) : (
            (d.weak_points || []).map((wp, i) => (
              <React.Fragment key={wp.label}>
                {i > 0 && <Divider />}
                <Stack direction="row" alignItems="center" justifyContent="space-between" sx={{ py: 1 }}>
                  <Typography variant="body2" sx={{ fontWeight: 600 }}>{wp.label}</Typography>
                  <Typography variant="caption" color="text.secondary" sx={{ flexShrink: 0, ml: 2 }}>
                    {wp.count === 1 ? 'once' : `${wp.count} times`}
                    {wp.last_seen ? ` · last ${new Date(wp.last_seen).toLocaleDateString()}` : ''}
                  </Typography>
                </Stack>
              </React.Fragment>
            ))
          )}
        </CardContent>
      </Card>

      {/* 5 · Logbook */}
      <Stack direction="row" alignItems="center" justifyContent="space-between" sx={{ mt: 3, mb: 1 }}>
        <Typography variant="subtitle2" color="text.secondary" sx={{ letterSpacing: '0.08em' }}>
          LOGBOOK
        </Typography>
        <Stack direction="row" spacing={1}>
          <Button size="small" startIcon={<BookIcon />} onClick={() => navigate('/journal')}>Journal</Button>
          <Button size="small" startIcon={<HistoryIcon />} onClick={() => navigate('/sessions')}>All sessions</Button>
        </Stack>
      </Stack>
      <Card elevation={0} sx={{ border: '1px solid', borderColor: 'divider', borderRadius: 2 }}>
        <CardContent sx={{ py: 1 }}>
          {recentSessions.length === 0 ? (
            <Typography variant="body2" color="text.secondary" sx={{ py: 3, textAlign: 'center' }}>
              No entries yet — your conversations and stories will appear here.
            </Typography>
          ) : (
            recentSessions.map((s, i) => (
              <React.Fragment key={s.session_id}>
                {i > 0 && <Divider />}
                <Stack direction="row" alignItems="center" justifyContent="space-between"
                  sx={{ py: 1.25, cursor: 'pointer' }}
                  onClick={() => navigate(`/session/${s.session_id}`)}>
                  <Box sx={{ minWidth: 0 }}>
                    <Typography variant="body2" sx={{ fontWeight: 600 }} noWrap>{s.content_title}</Typography>
                    <Typography variant="caption" color="text.secondary">
                      {s.content_type === 'story' ? 'Story' : 'Conversation'} · {new Date(s.updated_at).toLocaleDateString()}
                    </Typography>
                  </Box>
                  <ArrowForwardIcon sx={{ fontSize: 18, color: 'text.secondary', flexShrink: 0 }} />
                </Stack>
              </React.Fragment>
            ))
          )}
        </CardContent>
      </Card>
    </Box>
  );
}

function LegendDot({ color, label }) {
  return (
    <Box sx={{ display: 'flex', alignItems: 'center', gap: 0.5 }}>
      <Box sx={{ width: 10, height: 10, borderRadius: '50%', bgcolor: color }} />
      <Typography variant="caption" color="text.secondary">{label}</Typography>
    </Box>
  );
}
